import { useState } from 'react';
import {
  Container,
  Typography,
  Alert,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Fab,
  Drawer,
  IconButton,
  Badge,
} from '@mui/material';
import {
  CalendarToday,
  Search,
  Block,
  FilterList,
  Close,
  TuneRounded,
} from '@mui/icons-material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { AttestationList } from '../../components/AttestationList';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import { useHome, DATE_OPTIONS, STATUS_OPTIONS, TODAY_OPTIONS } from './useHome';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { styles } from './styles';

export const Home = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const {
    hasTeam,
    isLoading,
    filteredAttestations,
    filters,
    statistics,
    activeStatusOption,
    activeDateOption,
    todayFilter,
    handleDateFilter,
    handleStatusFilter,
    handleSearchChange,
    handleTodayFilter,
    clearAllFilters,
  } = useHome();

  const activeFiltersCount = [activeDateOption, activeStatusOption, todayFilter].filter(Boolean).length;

  if (!hasTeam) {
    return (
      <Container maxWidth="lg" sx={styles.container}>
        <Alert severity="info" sx={styles.alert}>
          Nenhum colaborador associado à sua gestão
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Typography variant="h4" component="h1" sx={styles.title}>
        Painel do Gestor - Atestados
      </Typography>

      {/* The Painel */}
      <Box sx={styles.bigNumbersGrid}>
        <Card sx={styles.bigNumberCard}>
          <CardContent>
            <Typography variant="body2" sx={styles.bigNumberLabel}>
              <DoneAllIcon sx={{ fontSize: { xs: 18, md: 24 }, color: '#205B56' }} />
              Aprovados
            </Typography>
            <Typography variant="h4" sx={styles.bigNumberValue}>
              {statistics.approved}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={styles.bigNumberCard}>
          <CardContent>
            <Typography variant="body2" sx={styles.bigNumberLabel}>
              <Block sx={{ fontSize: { xs: 18, md: 24 }, color: '#205B56' }} />
              Reprovados
            </Typography>
            <Typography variant="h4" sx={styles.bigNumberValue}>
              {statistics.rejected}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={styles.bigNumberCard}>
          <CardContent>
            <Typography variant="body2" sx={styles.bigNumberLabel}>
              <AccessTimeIcon sx={{ fontSize: { xs: 18, md: 24 }, color: '#205B56' }} />
              Em análise
            </Typography>
            <Typography variant="h4" sx={styles.bigNumberValue}>
              {statistics.inAnalysis}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={styles.bigNumberCard}>
          <CardContent>
            <Typography variant="body2" sx={styles.bigNumberLabel}>
              <GroupsIcon sx={{ fontSize: { xs: 18, md: 24 }, color: '#205B56' }} />
              Colaborador(es) com atestado hoje
            </Typography>
            <Typography variant="h4" sx={styles.bigNumberValue}>
              {statistics.employeesOnLeave}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search Field - Always visible */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Buscar por nome, CPF, matrícula ou e-mail"
          value={filters.searchText}
          onChange={handleSearchChange}
          sx={styles.searchField}
          InputProps={{
            startAdornment: <Search sx={{ color: '#94a3b8', mr: 1 }} />,
          }}
        />
      </Box>

      {/* Filter FAB */}
      <Fab
        onClick={() => setFilterOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: '#205B56',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(32, 91, 86, 0.3)',
          '&:hover': {
            backgroundColor: '#164039',
          },
          zIndex: 1000,
        }}
      >
        <Badge badgeContent={activeFiltersCount} color="error" overlap="circular">
          <TuneRounded />
        </Badge>
      </Fab>

      {/* Filter Drawer - Bottom Sheet */}
      <Drawer
        anchor="bottom"
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px 24px 0 0',
            maxHeight: '85vh',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            p: 0,
          },
        }}
      >
        <Box sx={{ p: 3, pb: 4 }}>
          {/* Handle bar */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ width: 40, height: 4, backgroundColor: '#e2e8f0', borderRadius: 2 }} />
          </Box>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TuneRounded sx={{ color: '#205B56' }} />
              Filtros
            </Typography>
            <IconButton onClick={() => setFilterOpen(false)} sx={{ color: '#64748b' }}>
              <Close />
            </IconButton>
          </Box>

          {/* Date Filter */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 600, color: '#475569', mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.95rem' }}>
              <CalendarToday sx={{ fontSize: '1.1rem', color: '#205B56' }} />
              Data de início do atestado
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {DATE_OPTIONS.map((option) => (
                <Button
                  key={option}
                  variant="contained"
                  onClick={() => handleDateFilter(option)}
                  sx={styles.dateFilterButton(activeDateOption === option)}
                >
                  {option}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Status Filter */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 600, color: '#475569', mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.95rem' }}>
              <FilterList sx={{ fontSize: '1.1rem', color: '#205B56' }} />
              Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {STATUS_OPTIONS.map((option) => (
                <Button
                  key={option}
                  variant="contained"
                  onClick={() => handleStatusFilter(option)}
                  sx={styles.dateFilterButton(activeStatusOption === option)}
                >
                  {option}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Today Filter */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 600, color: '#475569', mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.95rem' }}>
              <EventAvailableIcon sx={{ fontSize: '1.1rem', color: '#205B56' }} />
              Atestado com data de hoje
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {TODAY_OPTIONS.map((option) => (
                <Button
                  key={option}
                  variant="contained"
                  onClick={() => handleTodayFilter(option)}
                  sx={styles.dateFilterButton(todayFilter === option)}
                >
                  {option}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Apply Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={() => setFilterOpen(false)}
            sx={{
              mt: 2,
              py: 1.5,
              backgroundColor: '#205B56',
              fontWeight: 600,
              fontSize: '1rem',
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(32, 91, 86, 0.3)',
              '&:hover': {
                backgroundColor: '#164039',
              },
            }}
          >
            Aplicar Filtros
          </Button>

          {/* Clear Filters Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Close />}
            onClick={() => {
              clearAllFilters();
              setFilterOpen(false);
            }}
            sx={{
              mt: 1.5,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1rem',
              borderRadius: 2,
              textTransform: 'none',
              color: '#64748b',
              border: '2px solid #e2e8f0',
              '&:hover': {
                border: '2px solid #94a3b8',
                backgroundColor: '#f8fafc',
              },
            }}
          >
            Limpar Filtros
          </Button>
        </Box>
      </Drawer>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <AttestationList attestations={filteredAttestations} />
      )}
    </Container>
  );
};